import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Skeleton } from '@mui/material';
import { Icon, Typography } from '@scale/scaleui';
import _ from 'lodash';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

export interface StatisticCardProps {
  title: string;
  subtitle: string;
  value: number | string;
  valueSuffix?: string;
  valuePrefix?: string;
  delta?: number | string;
  deltaSuffix?: string;
  className?: string;
}

export const StatisticCard: React.FC<StatisticCardProps> = ({
  title,
  subtitle,
  value,
  valueSuffix,
  valuePrefix,
  delta,
  deltaSuffix,
  className,
}: StatisticCardProps) => {
  const deltaProps: { icon: IconProp; variant: 'neutral' | 'success' | 'primary'; color: string } =
    useMemo(() => {
      if (delta === undefined) {
        return {
          icon: 'minus-circle',
          variant: 'neutral',
          color: 'text-neutral-500',
        };
      }
      if (+delta > 0) {
        return {
          icon: 'arrow-up',
          variant: 'success',
          color: 'text-success-500',
        };
      }
      if (+delta < 0) {
        return {
          icon: 'arrow-down',
          variant: 'primary',
          color: 'text-primary-500',
        };
      }
      return {
        icon: ['far', 'minus-circle'],
        variant: 'neutral',
        color: 'text-neutral-500',
      };
    }, [delta]);

  return (
    <div
      className={twMerge(
        'p-4 border border-neutral-200 bg-neutral-0 rounded-md text-center w-[150px] h-[150px]',
        className,
      )}
    >
      <div className="h-full flex flex-col items-center justify-between">
        <div>
          <Typography.Subtitle2>{title}</Typography.Subtitle2>
          <Typography.Subtitle4>{subtitle}</Typography.Subtitle4>
        </div>
        <div>
          <div className={`${+value > 10000 ? 'text-3xl' : 'text-4xl'} text-medium`}>
            {valuePrefix}
            {_.isNil(value) ? '-' : value.toLocaleString()}
            {valueSuffix}
          </div>
        </div>
        {delta === undefined ? (
          <div className="h-2" />
        ) : (
          <div className="h-6">
            <div className={`flex flex-row items-center justify-center`}>
              <Icon className="opacity-0" size="sm" icon={deltaProps.icon} />
              {/*
                  // @ts-ignore */}
              <Typography.Subtitle2 className={`${deltaProps.color}`}>
                {delta}
                {deltaSuffix}
              </Typography.Subtitle2>
              <Icon size="sm" variant={deltaProps.variant} icon={deltaProps.icon} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const LoadingStatisticCard: React.FC = () => {
  return (
    <div
      className="p-4 border border-neutral-200 bg-neutral-0 rounded-md text-center"
      style={{ width: 160, height: 160 }}
    >
      <div className="h-full flex flex-col items-center justify-between">
        <div>
          <Typography.Subtitle2>
            <Skeleton width="15ex" />
          </Typography.Subtitle2>
          <Typography.Subtitle4>
            <Skeleton width="20ex" />
          </Typography.Subtitle4>
        </div>
        <div>
          <div className="text-5xl text-medium">
            <Skeleton width="3ex" />
          </div>
        </div>
        <div className="h-6">
          <Skeleton width="2ex" />
        </div>
      </div>
    </div>
  );
};
